import {
    AxiosResponseHeaders,
    HttpStatusCode,
    RawAxiosResponseHeaders,
} from "axios";
import objectHash from "object-hash";

import { MaybeType, createMayBeFor } from "@utils/maybe";
import { FacetsConfiguration } from "@utils/search/@types/searchConfiguration";
import { SearchRequest } from "@utils/search/@types/searchRequest";
import { SearchResult } from "@utils/search/@types/searchResult";

const sortObject = <TObject extends Record<string, unknown>>(obj: TObject) =>
    Object.keys(obj)
        .sort()
        .reduce((finalObject, key) => {
            finalObject[key] = obj[key];
            return finalObject;
        }, {} as Record<string, unknown>) as TObject;

export type CachedSearchResult = {
    status: HttpStatusCode;
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
    data: SearchResult;
};

export class SearchCache {
    private _entries: Record<string, CachedSearchResult> = {};

    public get(request: SearchRequest): CachedSearchResult | undefined {
        const key = this._createKey(request);
        return this._entries[key];
    }

    public set(request: SearchRequest, result: CachedSearchResult) {
        const key = this._createKey(request);
        this._entries[key] = result;
    }

    private _createKey(request: SearchRequest): string {
        const sortedRequest = {
            ...request,
            components: request.components
                .map(component => {
                    const mayBeFacetsConfiguration =
                        createMayBeFor<FacetsConfiguration>(
                            ["facetFilters", "numericFilters"],
                            component,
                        );

                    if (mayBeFacetsConfiguration.type === MaybeType.Nothing) {
                        return sortObject(component);
                    }

                    return {
                        ...mayBeFacetsConfiguration.value,
                        facetFilters:
                            mayBeFacetsConfiguration.value.facetFilters
                                .map(facetFilter =>
                                    sortObject({
                                        ...facetFilter,
                                        selectedValues:
                                            facetFilter.selectedValues.sort(),
                                    }),
                                )
                                .sort((a, b) => a.name.localeCompare(b.name)),
                        numericFilters:
                            mayBeFacetsConfiguration.value.numericFilters
                                .map(numericFilter => sortObject(numericFilter))
                                .sort((a, b) => a.name.localeCompare(b.name)),
                    };
                })
                .sort((a, b) => a.component.localeCompare(b.component)),
        };

        return objectHash.MD5(sortedRequest);
    }
}
